import { Button, Dialog, DialogContent, DialogTitle, Divider } from "@mui/material";
import { useState } from "react";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import DialogButtons from "@entrilia/shared/components/DialogButtons";
import DialogCloseButton from "@entrilia/shared/components/DialogCloseButton";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import { logError } from "@entrilia/shared/logging";
import { arraysHaveSameItems } from "@entrilia/shared/utilities/arrayHelper";
import adminApi from "../../../../../../api/adminApi";
import type { FundraisingAccessItem } from "../../../../../../api/types/fundraisingTypes";
import type { AccessValue } from "../../../../../common/AccessEditor";
import AccessEditor from "../../../../../common/AccessEditor";
import useFundraisingDetailsPageContext from "../useFundraisingDetailsPageContext";

interface Props {
  editedAccessItem: FundraisingAccessItem;
  onSubmit: () => void;
  onClose: () => void;
}

const getInitialAccessValue = (editedAccessItem: FundraisingAccessItem): AccessValue => ({
  selectedRole: editedAccessItem.roles[0] ?? "LP_Member",
  selectedCategoryIds: editedAccessItem.categoryIds,
});

const updateFundInvestor = withErrorHandling(adminApi.updateFundInvestor);

const EditFundraisingAccessDialog = ({ editedAccessItem, onSubmit, onClose }: Props) => {
  const { sendNotificationError } = useNotificationContext();
  const { fundraisingCategories } = useFundraisingDetailsPageContext();

  const [accessValue, setAccessValue] = useState<AccessValue>(() => getInitialAccessValue(editedAccessItem));
  const [isSaving, setIsSaving] = useState(false);

  const handleSave = async () => {
    setIsSaving(true);

    const [, error] = await updateFundInvestor(editedAccessItem.fundInvestorId, {
      existingContacts: [
        {
          contactId: editedAccessItem.contactId,
          roles:
            accessValue.selectedRole === "None" || accessValue.selectedRole === undefined
              ? []
              : [accessValue.selectedRole],
          externalCategoryIds: accessValue.selectedCategoryIds,
        },
      ],
      newContacts: [],
      removedContactIds: [],
    });

    setIsSaving(false);

    if (error) {
      logError(error, "[EditFundraisingAccessDialog] updateFundInvestor");
      sendNotificationError(error.message);
      return;
    }

    onSubmit();
    onClose();
  };

  const handleAccessChange = (update: Partial<AccessValue>) => {
    setAccessValue((prev) => ({ ...prev, ...update }));
  };

  const isDirty = !arraysHaveSameItems(editedAccessItem.categoryIds, accessValue.selectedCategoryIds);

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Edit {editedAccessItem.contactName}</DialogTitle>
      <DialogCloseButton onClick={onClose} />
      <Divider />

      <DialogContent>
        <AccessEditor
          value={accessValue}
          onChange={handleAccessChange}
          roleOptions={["LP_Member"]}
          categoryOptions={fundraisingCategories.map((c) => ({ id: c.externalId, label: c.name }))}
        />
      </DialogContent>

      <Divider />
      <DialogButtons onCancel={onClose}>
        <Button variant="contained" loading={isSaving} onClick={handleSave} disabled={!isDirty}>
          Save
        </Button>
      </DialogButtons>
    </Dialog>
  );
};

export default EditFundraisingAccessDialog;
